/**
 * Video utility functions for LetsBlur
 */

import { API_URL } from './constants';

/**
 * Video configuration
 */
export const VIDEO_CONFIG = {
  maxDurationSeconds: 600,
  maxSizeMB: 50,
  supportedFormats: ['mp4', 'mov', 'avi'],
  supportedMimeTypes: ['video/mp4', 'video/quicktime', 'video/x-msvideo'],
};

/**
 * WebSocket URL for progress updates
 */
export const WS_URL = API_URL.replace('http://', 'ws://').replace('https://', 'wss://');

/**
 * Generate a thumbnail from a video file
 * @param {File} videoFile - The video file
 * @param {number} seekTime - Time in seconds to capture thumbnail (default: 1)
 * @returns {Promise<string>} - Data URL of the thumbnail
 */
export async function generateVideoThumbnail(videoFile, seekTime = 1) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    const objectUrl = URL.createObjectURL(videoFile);
    video.src = objectUrl;

    video.onloadedmetadata = () => {
      // Seek to the specified time or 10% of duration
      const targetTime = Math.min(seekTime, video.duration * 0.1);
      video.currentTime = targetTime;
    };

    video.onseeked = () => {
      // Set canvas size to maintain aspect ratio
      const maxSize = 200;
      let width = video.videoWidth;
      let height = video.videoHeight;

      if (width > height) {
        if (width > maxSize) {
          height = (height / width) * maxSize;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width / height) * maxSize;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(video, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

      URL.revokeObjectURL(objectUrl);
      resolve(dataUrl);
    };

    video.onerror = (e) => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video'));
    };

    // Timeout after 10 seconds
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Thumbnail generation timed out'));
    }, 10000);
  });
}

/**
 * Get video metadata from a file
 * @param {File} videoFile - The video file
 * @returns {Promise<Object>} - Video metadata
 */
export async function getVideoMetadata(videoFile) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    const objectUrl = URL.createObjectURL(videoFile);
    video.src = objectUrl;

    video.onloadedmetadata = () => {
      const metadata = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        aspectRatio: video.videoWidth / video.videoHeight,
      };

      URL.revokeObjectURL(objectUrl);
      resolve(metadata);
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video metadata'));
    };
  });
}

/**
 * Format duration in seconds to mm:ss or hh:mm:ss
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration
 */
export function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Calculate estimated time remaining
 * @param {number} progress - Current progress (0-100)
 * @param {number} elapsedMs - Elapsed time in milliseconds
 * @returns {number} - Estimated seconds remaining
 */
export function calculateETA(progress, elapsedMs) {
  if (progress <= 0 || elapsedMs <= 0) return 0;

  const elapsedSeconds = elapsedMs / 1000;
  const totalEstimated = (elapsedSeconds / progress) * 100;
  const remaining = totalEstimated - elapsedSeconds;

  return Math.max(0, Math.round(remaining));
}

/**
 * Validate a video file
 * @param {File} file - The file to validate
 * @param {Object} config - Optional config overrides
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export function validateVideoFile(file, config = {}) {
  const errors = [];
  const maxSize = (config.maxSizeMB || VIDEO_CONFIG.maxSizeMB) * 1024 * 1024;
  const supportedTypes = config.supportedMimeTypes || VIDEO_CONFIG.supportedMimeTypes;

  // Check file type
  if (!file.type.startsWith('video/')) {
    errors.push('File is not a video');
  } else if (!supportedTypes.some(type => file.type.includes(type.split('/')[1]))) {
    errors.push(`Unsupported video format. Supported: ${VIDEO_CONFIG.supportedFormats.join(', ')}`);
  }

  // Check file size
  if (file.size > maxSize) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    errors.push(`Video too large: ${sizeMB}MB. Maximum: ${config.maxSizeMB || VIDEO_CONFIG.maxSizeMB}MB`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate video duration
 * @param {number} duration - Duration in seconds
 * @param {number} maxDuration - Maximum allowed duration
 * @returns {Object} - { valid: boolean, error: string }
 */
export function validateVideoDuration(duration, maxDuration = VIDEO_CONFIG.maxDurationSeconds) {
  if (duration > maxDuration) {
    return {
      valid: false,
      error: `Video too long: ${formatDuration(duration)}. Maximum: ${formatDuration(maxDuration)}`,
    };
  }
  return { valid: true, error: null };
}

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Check if a file is a video
 * @param {File} file - The file to check
 * @returns {boolean}
 */
export function isVideoFile(file) {
  return file.type.startsWith('video/');
}

/**
 * Video API functions
 */
export const videoAPI = {
  /**
   * Upload a video file
   */
  async upload(file, userSessionId = '') {
    const formData = new FormData();
    formData.append('file', file);
    if (userSessionId) {
      formData.append('user_session_id', userSessionId);
    }

    const response = await fetch(`${API_URL}/api/v1/video/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  },

  /**
   * Start video detection with optional trim range
   */
  async startDetection(sessionId, fileId, enableGender = true, startSeconds = 0, endSeconds = 0) {
    let response;
    try {
      response = await fetch(
        `${API_URL}/api/v1/video/detect/${sessionId}/${fileId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            start_seconds: startSeconds,
            end_seconds: endSeconds,
            enable_gender: enableGender
          })
        }
      );
    } catch (err) {
      throw new Error('Cannot connect to server. Please check if the backend is running.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Detection failed to start');
    }

    return response.json();
  },

  /**
   * Get detection status
   */
  async getDetectionStatus(jobId) {
    const response = await fetch(`${API_URL}/api/v1/video/detect/status/${jobId}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to get status');
    }

    return response.json();
  },

  /**
   * Start video processing (blur)
   */
  async startProcessing(sessionId, fileId, blurSettings) {
    let response;
    try {
      response = await fetch(`${API_URL}/api/v1/video/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          file_id: fileId,
          blur_settings: blurSettings,
        }),
      });
    } catch (err) {
      throw new Error('Cannot connect to server. Please check if the backend is running.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Processing failed to start');
    }

    return response.json();
  },

  /**
   * Get processing status
   */
  async getProcessingStatus(jobId) {
    const response = await fetch(`${API_URL}/api/v1/video/process/status/${jobId}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to get status');
    }

    return response.json();
  },

  /**
   * Get preview URL
   */
  getPreviewUrl(sessionId, fileId) {
    return `${API_URL}/api/v1/video/preview/${sessionId}/${fileId}`;
  },

  /**
   * Get download URL
   */
  getDownloadUrl(sessionId, fileId) {
    return `${API_URL}/api/v1/video/download/${sessionId}/${fileId}`;
  },

  /**
   * Get thumbnail URL for a person
   */
  getThumbnailUrl(sessionId, fileId, personId) {
    return `${API_URL}/api/v1/video/thumbnail/${sessionId}/${fileId}/${personId}`;
  },

  /**
   * Get file info
   */
  async getFileInfo(sessionId, fileId) {
    const response = await fetch(`${API_URL}/api/v1/video/sessions/${sessionId}/files/${fileId}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to get file info');
    }

    return response.json();
  },

  /**
   * Delete session
   */
  async deleteSession(sessionId) {
    const response = await fetch(`${API_URL}/api/v1/video/session/${sessionId}`, {
      method: 'DELETE',
    });

    return response.ok;
  },
};
